import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Bar, CartesianGrid, ComposedChart, Line, XAxis, YAxis } from 'recharts';

import { useFormat } from '@nucleus-commerce/i18n/react';
import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '@nucleus-commerce/ui/components/chart';

export interface SupportPoint {
  label: string;
  answered: number;
  missed: number;
  tickets: number;
}

export interface SupportChartProps {
  points: readonly SupportPoint[];
  /** Read by screen readers in place of the chart. */
  summary: string;
}

/** Chats per day stacked answered over missed, with tickets opened as a line. */
export function SupportChart({ points, summary }: SupportChartProps) {
  const { t } = useTranslation('reports');
  const format = useFormat();

  const config = useMemo(
    () =>
      ({
        answered: { label: t('support.chart.answered'), color: 'var(--chart-1)' },
        missed: { label: t('support.chart.missed'), color: 'var(--chart-4)' },
        tickets: { label: t('support.chart.tickets'), color: 'var(--chart-2)' },
      }) satisfies ChartConfig,
    [t],
  );

  return (
    <figure className="m-0">
      <figcaption className="sr-only">{summary}</figcaption>
      <ChartContainer config={config} className="aspect-auto h-[280px] w-full" aria-hidden>
        <ComposedChart data={[...points]} margin={{ left: 4, right: 4, top: 8 }}>
          <CartesianGrid vertical={false} />
          <XAxis dataKey="label" tickLine={false} axisLine={false} minTickGap={16} />
          <YAxis tickLine={false} axisLine={false} allowDecimals={false} width={32} />
          <ChartTooltip
            content={
              <ChartTooltipContent
                formatter={(value, name) => (
                  <div className="flex w-full items-center justify-between gap-3">
                    <span className="text-muted-foreground">
                      {t(`support.chart.${String(name) as 'answered' | 'missed' | 'tickets'}`)}
                    </span>
                    <span className="font-mono font-medium tabular-nums">
                      {format.number(Number(value))}
                    </span>
                  </div>
                )}
              />
            }
          />
          <ChartLegend content={<ChartLegendContent />} />
          <Bar dataKey="answered" stackId="chats" fill="var(--color-answered)" />
          <Bar dataKey="missed" stackId="chats" fill="var(--color-missed)" radius={[3, 3, 0, 0]} />
          <Line
            dataKey="tickets"
            type="monotone"
            stroke="var(--color-tickets)"
            strokeWidth={2}
            dot={false}
          />
        </ComposedChart>
      </ChartContainer>
    </figure>
  );
}
